import * as React from 'react';
import { Box, Drawer } from '@mui/material';
import UsersTable from './UsersTable';
import AddUserDrawer from './AddUserDrawer';
import ImportUsersFromCsvDrawer from './ImportUsersFromCsvDrawer';
import AssignUsersToCompanyDialog, { CompanyOption } from './AssignUsersToCompanyDialog';
import { Role, Group } from '@esg/esg-global-types';
import { getRoles } from '../../lib/user_management/roles';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import { UserContext } from '../../context/UserContext';
import { getAdminGroups } from '../../util/user_access';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import { useSubscriptionContext } from '../../context/SubscriptionContext';
import AlertBanner from '../shared/banner/AlertBanner';
import { ExceededQuotaKey } from '../../lib/app/subscription';
import { getGroups } from '../../graphql/services/group.service';
/**
 * Wrapper for user management functions and display grid.
 * @returns {JSX.Element}
 */
const ManageUsersHub = () => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { user } = React.useContext(UserContext);
  const [groups, setGroups] = React.useState<Group[]>([]);
  const [roles, setRoles] = React.useState<Role[]>([]);
  const [showCreateUser, setShowCreateUser] = React.useState<boolean>(false);
  const [showImportCsv, setShowImportCsv] = React.useState<boolean>(false);
  const [showAssign, setShowAssign] = React.useState<boolean>(false);
  const adminGroups: Array<string> = user ? getAdminGroups(user) : [];

  // Bumped after Add User / CSV import so UsersTable jumps its own paginated
  // fetch back to page 1 — the list no longer holds a copy of the user rows
  // itself, so there's nothing here to refetch into.
  const [refreshSignal, setRefreshSignal] = React.useState<number>(0);
  const triggerRefresh = () => setRefreshSignal((n) => n + 1);

  const { subscription, getExceededSubscriptionKeys } = useSubscriptionContext();
  const [showQuotaExceededAlert, setShowQuotaExceededAlert] = React.useState(false);

  const handleClose = () => {
    setShowQuotaExceededAlert(false);
  };

  const exceededSubscriptionKeys: Array<ExceededQuotaKey> = getExceededSubscriptionKeys([
    'internal_users'
  ]);

  React.useEffect(() => {
    if (exceededSubscriptionKeys.length) {
      setShowQuotaExceededAlert(true);
    }
  }, [subscription]);

  React.useEffect(() => {
    void fetchGroupsAndRoles();
  }, []);

  const fetchGroupsAndRoles = async () => {
    if (!user) return;
    try {
      const [fetchedGroups, fetchedRoles]: [Group[], Role[]] = await Promise.all([
        getGroups(),
        getRoles()
      ]);
      setGroups(fetchedGroups);
      setRoles(fetchedRoles);
    } catch (err: unknown) {
      const trackingId: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ManageUsersHub failed on an unknown error while fetching groups/roles.',
          { user: user },
          trackingId
        )
      );
      setFeedbackData({
        message: `Unable to fetch groups/roles. Tracking ID: ${trackingId}`,
        state: true,
        type: 'error'
      });
    }
  };

  const openCreateDrawer = async () => {
    setShowCreateUser(true);
  };

  const filteredGroups: Array<Group> = groups.filter((g) => {
    if (user?.super_admin) return true;
    return adminGroups.includes(g.id);
  });

  const assignCompanies: Array<CompanyOption> = React.useMemo(() => {
    const opts: Array<CompanyOption> = [];
    const seen = new Set<string>();
    filteredGroups.forEach((g) =>
      (g.companies ?? []).forEach((c) => {
        if (!seen.has(c.id)) {
          seen.add(c.id);
          opts.push({ id: c.id, name: c.name, group_id: g.id, group_name: g.name });
        }
      })
    );
    return opts.sort((a, b) => a.name.localeCompare(b.name));
  }, [groups]);

  return (
    <>
      <Drawer
        anchor={'right'}
        open={showCreateUser}
        onClose={() => setShowCreateUser(false)}
        PaperProps={{ style: { width: 480 } }}
      >
        <AddUserDrawer
          groups={filteredGroups}
          roles={roles}
          currentUser={user}
          onCreated={() => triggerRefresh()}
          onClose={() => setShowCreateUser(false)}
        />
      </Drawer>

      <Drawer
        anchor="right"
        open={showImportCsv}
        onClose={() => setShowImportCsv(false)}
        PaperProps={{ style: { width: 960 } }}
        ModalProps={{ disableEnforceFocus: true }}
      >
        <ImportUsersFromCsvDrawer
          groups={filteredGroups}
          roles={roles}
          onImported={() => triggerRefresh()}
          onClose={() => setShowImportCsv(false)}
        />
      </Drawer>

      <Box
        sx={{
          textAlign: 'start',
          padding: '2%',
          display: 'flex',
          flexDirection: 'column'
        }}
        className="div-metrics-hub"
      >
        <AlertBanner
          severity="warning"
          message={`Quota reached for ${exceededSubscriptionKeys
            .map((item) => `${item.label} (${subscription?.quota[item.key]})`)
            .join(', ')} allocated to your subscription.`}
          open={showQuotaExceededAlert && !!exceededSubscriptionKeys.length}
          action={handleClose}
        />
        <AssignUsersToCompanyDialog
          open={showAssign}
          onClose={() => setShowAssign(false)}
          companies={assignCompanies}
          roles={roles}
          onDone={() => triggerRefresh()}
        />
        <UsersTable
          groups={filteredGroups}
          all_groups={groups}
          roles={roles}
          handleCreateUser={openCreateDrawer}
          refreshSignal={refreshSignal}
          onOpenImportCsv={() => setShowImportCsv(true)}
          onAssignToCompany={() => setShowAssign(true)}
        />
      </Box>
    </>
  );
};

export default ManageUsersHub;
